import { NavLink } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useData } from '../../contexts/DataContext';
import { useLocation, useNavigate } from 'react-router-dom';
import { usePermissions } from '../../contexts/PermissionsContext';
import { useAuth } from '../../contexts/AuthContext';
import { supabase } from '../../services/supabase';
import LanguageSwitcher from '../LanguageSwitcher';
import { useNotification } from '../../contexts/NotificationContext';
import { useEffect, useState } from 'react';
import WishlistModal from '../Wishlist';
import { useFeatureFlag, FeatureFlag } from '../../utils/featureFlags';
import CompanySelector from '../shared/CompanySelector';

interface SidebarProps {
  isCollapsed: boolean;
  onToggle: () => void;
}

type SectionState = {
  aiTools: boolean;
  integrations: boolean;
  settings: boolean;
};

const Sidebar = ({ isCollapsed, onToggle }: SidebarProps) => {
  const { t } = useTranslation();
  const { isSuperAdmin, isAdminForCompany } = usePermissions();
  const { targetCompany } = useData();
  const location = useLocation();
  const navigate = useNavigate();
  const { user } = useAuth();
  const { showNotification } = useNotification();
  const [isWishlistOpen, setIsWishlistOpen] = useState(false);
  const isCustobarEnabled = useFeatureFlag(FeatureFlag.CUSTOBAR_DATA_INGESTION);

  // Dropdown states for sections - load from localStorage

  const [sectionsOpen, setSectionsOpen] = useState<SectionState>(() => {
    const saved = localStorage.getItem('sidebar-sections-state');
    if (saved) {
      try {
        return JSON.parse(saved) as SectionState;
      } catch {
        console.error('Error parsing sidebar sections state');
      }
    }
    return {
      aiTools: true,
      integrations: true,
      settings: true,
    };
  });

  const toggleSection = (section: keyof SectionState) => {
    setSectionsOpen((prev: SectionState) => {
      const newState: SectionState = {
        ...prev,
        [section]: !prev[section],
      };
      // Save to localStorage
      localStorage.setItem('sidebar-sections-state', JSON.stringify(newState));
      return newState;
    });
  };

  const { refreshData } = useData();

  useEffect(() => {
    refreshData();
  }, []);

  // AI Tools section - all current items except management
  const aiToolsItems = [
    { path: '/chat', label: t('sidebar.chat'), icon: 'chat', color: 'text-green-500' },
    {
      path: '/social-media-posts',
      label: t('sidebar.socialMediaPosts'),
      icon: 'campaign',
      color: 'text-red-500',
    },
    {
      path: '/ad-generation',
      label: t('sidebar.adGeneration'),
      icon: 'campaign',
      color: 'text-blue-500',
    },
    { path: '/newsletter', label: t('sidebar.newsletter'), icon: 'mail', color: 'text-yellow-500' },
    {
      path: '/article-generation',
      label: t('sidebar.articleGeneration'),
      icon: 'article',
      color: 'text-indigo-500',
    },
    {
      path: '/product-description',
      label: t('sidebar.productDescription'),
      icon: 'description',
      color: 'text-pink-500',
    },
    {
      path: '/category-description',
      label: t('sidebar.categoryDescription'),
      icon: 'category',
      color: 'text-emerald-500',
    },
  ];

  // Integrations section - Custobar (conditionally shown)
  const integrationsItems = [
    ...(isCustobarEnabled
      ? [
          {
            path: '/custobar',
            label: t('sidebar.custobar'),
            icon: 'integration_instructions',
            color: 'text-purple-500',
          },
        ]
      : []),
  ];

  // Settings section - Management
  const settingsItems = [
    ...(isSuperAdmin() || isAdminForCompany(targetCompany || undefined)
      ? [
          {
            path: '/companies',
            label: t('sidebar.companies'),
            icon: 'business',
            color: 'text-blue-500',
          },
        ]
      : []),
    ...(isSuperAdmin()
      ? [
          {
            path: '/users',
            label: t('sidebar.users'),
            icon: 'people',
            color: 'text-green-500',
          },
        ]
      : []),
    ...(isSuperAdmin()
      ? [
          {
            path: '/system-wishlist',
            label: t('sidebar.system_wishlist'),
            icon: 'star',
            color: 'text-yellow-500',
          },
        ]
      : []),
    ...(isSuperAdmin()
      ? [
          {
            path: '/system-prompts',
            label: t('sidebar.system_prompts'),
            icon: 'psychology',
            color: 'text-purple-500',
          },
        ]
      : []),
    ...(isSuperAdmin()
      ? [
          {
            path: '/ai-models',
            label: t('sidebar.ai_models'),
            icon: 'smart_toy',
            color: 'text-blue-500',
          },
        ]
      : []),
  ];

  const handleSignOut = async () => {
    try {
      const { error } = await supabase.auth.signOut();
      if (error) throw error;
      navigate('/login');
    } catch (error) {
      console.error('Error signing out:', error);
      showNotification(t('common.error_signing_out'), 'error');
    }
  };

  return (
    <aside
      className={`bg-gray-200 text-gray-800 transition-all duration-300 ease-in-out ${
        isCollapsed ? 'w-20' : 'w-64'
      } fixed left-0 h-full shadow-lg z-10 flex flex-col`}
    >
      <button
        onClick={onToggle}
        className="absolute -right-3 top-20 bg-gray-200 text-gray-800 p-1 hover:bg-gray-700 shadow-md transition-all duration-200 ease-in-out hover:scale-110 rounded-full"
        aria-label={isCollapsed ? t('sidebar.expand') : t('sidebar.collapse')}
      >
        <span className="material-symbols-outlined text-sm transition-transform duration-300 ease-in-out">
          {isCollapsed ? 'keyboard_double_arrow_right' : 'keyboard_double_arrow_left'}
        </span>
      </button>
      <div
        className={`flex items-center p-4 border-b dark:border-gray-700 h-16 transition-all duration-300 ease-in-out`}
      >
        <img
          src="/hat-logo.png"
          alt={t('sidebar.logo_alt')}
          className="h-8 w-8 mr-2 transition-all duration-300 ease-in-out"
        />
        <div
          className={`transition-all duration-300 ease-in-out ${
            isCollapsed ? 'w-0 opacity-0' : 'w-full opacity-100'
          }`}
        >
          {!location.pathname.startsWith('/management') &&
            !location.pathname.startsWith('/company') && (
              <CompanySelector
                className="pl-4 pt-2 flex-grow whitespace-nowrap"
                showColorIndicator={true}
                colorOverride="bg-green-500"
              />
            )}
        </div>
      </div>

      <nav className="flex flex-col mt-3">
        {/* AI Tools Section */}
        {aiToolsItems.length > 0 && (
          <div className="mb-2">
            <button
              onClick={() => toggleSection('aiTools')}
              className="w-full flex items-center justify-between px-4 py-2 text-xs font-semibold text-gray-500 uppercase tracking-wider hover:text-gray-700 transition-colors duration-200"
            >
              <div
                className={`overflow-hidden transition-all duration-300 ease-in-out ${
                  isCollapsed ? 'w-0 opacity-0' : 'w-auto opacity-100'
                }`}
              >
                <span className="whitespace-nowrap">{t('sidebar.sections.aiTools')}</span>
              </div>
              <span
                className={`material-symbols-outlined text-sm transition-transform duration-200 ${
                  sectionsOpen.aiTools ? 'rotate-180' : ''
                }`}
              >
                expand_more
              </span>
            </button>

            <div
              className={`overflow-hidden transition-all duration-300 ease-in-out ${
                sectionsOpen.aiTools ? 'max-h-96 opacity-100' : 'max-h-0 opacity-0'
              }`}
            >
              <div className="pb-2">
                {aiToolsItems.map((item) => (
                  <NavLink
                    key={item.path}
                    to={item.path}
                    className={({ isActive }) =>
                      `flex items-center p-2 mx-2 rounded-md transition-all duration-200 ease-in-out ${
                        isActive ? 'bg-gray-300 text-white' : 'hover:bg-gray-200'
                      } ${isCollapsed ? 'justify-center' : 'justify-start'}`
                    }
                  >
                    <span
                      className={`material-symbols-outlined ${item.color} text-xl transition-all duration-200 ease-in-out ${
                        isCollapsed ? 'mr-0' : 'mr-2'
                      }`}
                    >
                      {item.icon}
                    </span>
                    <div
                      className={`overflow-hidden transition-all duration-300 ease-in-out ${
                        isCollapsed ? 'w-0 opacity-0' : 'w-auto opacity-100'
                      }`}
                    >
                      <span className="ml-3 whitespace-nowrap font-medium">{item.label}</span>
                    </div>
                  </NavLink>
                ))}
              </div>
            </div>
          </div>
        )}

        {/* Integrations Section */}
        {integrationsItems.length > 0 && (
          <div className="mb-2">
            <button
              onClick={() => toggleSection('integrations')}
              className="w-full flex items-center justify-between px-4 py-2 text-xs font-semibold text-gray-500 uppercase tracking-wider hover:text-gray-700 transition-colors duration-200"
            >
              <div
                className={`overflow-hidden transition-all duration-300 ease-in-out ${
                  isCollapsed ? 'w-0 opacity-0' : 'w-auto opacity-100'
                }`}
              >
                <span className="whitespace-nowrap">{t('sidebar.sections.integrations')}</span>
              </div>
              <span
                className={`material-symbols-outlined text-sm transition-transform duration-200 ${
                  sectionsOpen.integrations ? 'rotate-180' : ''
                }`}
              >
                expand_more
              </span>
            </button>

            <div
              className={`overflow-hidden transition-all duration-300 ease-in-out ${
                sectionsOpen.integrations ? 'max-h-96 opacity-100' : 'max-h-0 opacity-0'
              }`}
            >
              <div className="pb-2">
                {integrationsItems.map((item) => (
                  <NavLink
                    key={item.path}
                    to={item.path}
                    className={({ isActive }) =>
                      `flex items-center p-2 mx-2 rounded-md transition-all duration-200 ease-in-out ${
                        isActive ? 'bg-gray-300 text-white' : 'hover:bg-gray-200'
                      } ${isCollapsed ? 'justify-center' : 'justify-start'}`
                    }
                  >
                    <span
                      className={`material-symbols-outlined ${item.color} text-xl transition-all duration-200 ease-in-out ${
                        isCollapsed ? 'mr-0' : 'mr-2'
                      }`}
                    >
                      {item.icon}
                    </span>
                    <div
                      className={`overflow-hidden transition-all duration-300 ease-in-out ${
                        isCollapsed ? 'w-0 opacity-0' : 'w-auto opacity-100'
                      }`}
                    >
                      <span className="ml-3 whitespace-nowrap font-medium">{item.label}</span>
                    </div>
                  </NavLink>
                ))}
              </div>
            </div>
          </div>
        )}

        {/* Settings Section */}
        {settingsItems.length > 0 && (
          <div className="mb-2">
            <button
              onClick={() => toggleSection('settings')}
              className="w-full flex items-center justify-between px-4 py-2 text-xs font-semibold text-gray-500 uppercase tracking-wider hover:text-gray-700 transition-colors duration-200"
            >
              <div
                className={`overflow-hidden transition-all duration-300 ease-in-out ${
                  isCollapsed ? 'w-0 opacity-0' : 'w-auto opacity-100'
                }`}
              >
                <span className="whitespace-nowrap">{t('sidebar.sections.settings')}</span>
              </div>
              <span
                className={`material-symbols-outlined text-sm transition-transform duration-200 ${
                  sectionsOpen.settings ? 'rotate-180' : ''
                }`}
              >
                expand_more
              </span>
            </button>

            <div
              className={`overflow-hidden transition-all duration-300 ease-in-out ${
                sectionsOpen.settings ? 'max-h-96 opacity-100' : 'max-h-0 opacity-0'
              }`}
            >
              <div className="pb-2">
                {settingsItems.map((item) => (
                  <NavLink
                    key={item.path}
                    to={item.path}
                    className={({ isActive }) => {
                      // For companies menu item, also consider it active when on individual company pages
                      const isCompaniesActive =
                        item.path === '/companies' &&
                        (isActive || location.pathname.startsWith('/company/'));
                      const finalIsActive =
                        item.path === '/companies' ? isCompaniesActive : isActive;

                      return `flex items-center p-2 mx-2 rounded-md transition-all duration-200 ease-in-out ${
                        finalIsActive ? 'bg-gray-300 text-white' : 'hover:bg-gray-200'
                      } ${isCollapsed ? 'justify-center' : 'justify-start'}`;
                    }}
                  >
                    <span
                      className={`material-symbols-outlined ${item.color} text-xl transition-all duration-200 ease-in-out ${
                        isCollapsed ? 'mr-0' : 'mr-2'
                      }`}
                    >
                      {item.icon}
                    </span>
                    <div
                      className={`overflow-hidden transition-all duration-300 ease-in-out ${
                        isCollapsed ? 'w-0 opacity-0' : 'w-auto opacity-100'
                      }`}
                    >
                      <span className="ml-3 whitespace-nowrap font-medium">{item.label}</span>
                    </div>
                  </NavLink>
                ))}
              </div>
            </div>
          </div>
        )}

        {/* Send a wish button */}
        <button
          onClick={() => setIsWishlistOpen(true)}
          className={`flex items-center p-2 mx-2 mt-2 rounded-md transition-all duration-200 ease-in-out hover:bg-gray-200 ${
            isCollapsed ? 'justify-center' : 'justify-start'
          }`}
        >
          <span
            className={`material-symbols-outlined text-purple-500 text-xl transition-all duration-200 ease-in-out ${
              isCollapsed ? 'mr-0' : 'mr-2'
            }`}
          >
            lightbulb
          </span>
          <div
            className={`overflow-hidden transition-all duration-300 ease-in-out ${
              isCollapsed ? 'w-0 opacity-0' : 'w-auto opacity-100'
            }`}
          >
            <span className="ml-3 whitespace-nowrap font-medium">{t('wishlist.send_wish')}</span>
          </div>
        </button>
      </nav>

      <hr className="mt-4 mb-4" />

      <div className="mt-auto border-t border-gray-300 p-4 transition-all duration-300 ease-in-out">
        <div className="flex items-center justify-between transition-all duration-300 ease-in-out">
          <LanguageSwitcher />
          {user && !isCollapsed && (
            <div className="relative group ml-4 animate-fadeIn">
              <button
                onClick={handleSignOut}
                className="text-gray-600 hover:text-gray-900 flex items-center transition-all duration-250 ease-in-out whitespace-nowrap"
              >
                <span className="material-symbols-outlined">logout</span>
                <span className="ml-2 text-xs whitespace-nowrap">{t('common.sign_out')}</span>
              </button>
              <div
                className="absolute right-0 w-max px-3 py-2 bg-gray-800 text-white text-sm rounded-md opacity-0 invisible group-hover:opacity-100 group-hover:visible transition-all duration-200 ease-in-out mt-2"
                style={{ bottom: '50px', left: '10px' }}
              >
                {t('common.logged_in_as')}: {user.email}
                <div className="mt-1 text-xs text-gray-400 break-all">
                  {import.meta.env.VITE_SUPABASE_URL}
                </div>
              </div>
            </div>
          )}
        </div>
      </div>

      {/* Wishlist Modal */}
      <WishlistModal isOpen={isWishlistOpen} onClose={() => setIsWishlistOpen(false)} />
    </aside>
  );
};

export default Sidebar;
